import { Suspense } from 'react';

import { Box, Container, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material';

import { type ObservableQuery } from '@apollo/client';
import { useQuery, useReactiveVar } from '@apollo/client/react';
import type { Theme } from '@kausal/themes/types';

import { isLocalDev } from '@common/env';
import { logApolloError } from '@common/logging/apollo';

import type { PageQuery, PageQueryVariables } from '@/common/__generated__/graphql';
import { activeGoalVar } from '@/common/cache';
import { useTranslation } from '@/common/i18n';
import { useFeatures } from '@/common/instance';
import ErrorMessage from '@/components/common/ErrorMessage';
import Error from '@/components/common/PathsError';
import { StreamField } from '@/components/common/StreamField';
import ActionListPage from '@/components/pages/ActionListPage';
import DashboardPage from '@/components/pages/DashboardPage';
import OutcomePage from '@/components/pages/OutcomePage';
import StaticPage from '@/components/pages/StaticPage';
import { useSiteOrNull } from '@/context/site';
import GET_PAGE from '@/queries/getPage';
import { getBaselineScenario, getProgressTrackingScenario } from '@/utils/progress-tracking';

export type PageRefetchCallback = ObservableQuery<PageQuery>['refetch'];

const PageLoader = ({ theme }: { theme: Theme }) => {
  return (
    <Box sx={{ py: 4, backgroundColor: theme.brandDark }}>
      <Container fixed maxWidth="xl">
        <Box
          sx={{
            height: '400px',
          }}
        >
          <Skeleton
            variant="rectangular"
            width="100%"
            height="240px"
            sx={{ backgroundColor: theme.graphColors.grey050, mb: 2 }}
          />
        </Box>
      </Container>
    </Box>
  );
};

type PageProps = {
  path: string;
  headerExtra?: React.JSX.Element;
};

function Page(props: PageProps) {
  const { path, headerExtra } = props;

  const site = useSiteOrNull();
  const { showRefreshPrompt } = useFeatures();
  const baselineScenario = site ? getBaselineScenario(site.scenarios) : undefined;
  const scenarios =
    site && showRefreshPrompt && !!getProgressTrackingScenario(site.scenarios)
      ? ['default', 'progress_tracking', ...(baselineScenario ? [baselineScenario.id] : [])]
      : null;
  const activeGoal = useReactiveVar(activeGoalVar);
  const queryResp = useQuery<PageQuery, PageQueryVariables>(GET_PAGE, {
    variables: {
      path,
    },
    context: {
      componentName: 'Page',
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const { loading, error, previousData, refetch } = queryResp;

  const data = queryResp.data ?? previousData;
  const { t } = useTranslation();
  const theme = useTheme();

  if (error) {
    logApolloError(error, { component: 'Page' });
    if (isLocalDev) {
      throw error;
    } else {
      return <Error statusCode={500} />;
    }
  }
  if (!data) {
    return <PageLoader theme={theme} />;
  }
  const { page, activeScenario } = data;
  let pageContent: React.ReactNode;
  if (!page) {
    console.error(`No page found for path ${path}`);
    return <Error statusCode={404} />;
  }

  if (page.__typename === 'DashboardPage') {
    pageContent = <DashboardPage page={page} />;
  } else if (page.__typename === 'OutcomePage') {
    pageContent = (
      <OutcomePage
        page={page}
        outcomeNodeId={page.outcomeNode.id}
        scenarios={scenarios}
        activeGoalId={activeGoal?.id ?? null}
        refetch={refetch}
        activeScenario={activeScenario}
        refetching={loading}
      />
    );
  } else if (page.__typename === 'ActionListPage') {
    pageContent = <ActionListPage page={page} refetch={refetch} />;
  } else if (page.__typename === 'StaticPage') {
    pageContent = <StaticPage page={page} refetch={refetch} />;
  } else if (page.__typename === 'InstanceRootPage') {
    pageContent = (
      <Container fixed maxWidth="xl">
        {page.body?.map((block) => (block ? <StreamField key={block.id} block={block} /> : null))}
      </Container>
    );
  } else {
    console.error('Invalid page type: ', page.__typename);
    return <ErrorMessage message={`${t('invalid-page-type')}: ${page.__typename}`} />;
  }
  return (
    <>
      {headerExtra}
      <Suspense fallback={<PageLoader theme={theme} />}>{pageContent}</Suspense>
    </>
  );
}

export default Page;
